import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import GridIndexStudentDetails from "../../../components/GridIndexStudentDetails";
import CustomAutocomplete from "../../../components/Inputs/CustomAutocomplete";
import useBreadcrumbs from "../../../hooks/useBreadcrumbs";
import useAlert from "../../../hooks/useAlert";
import useAcademicYearBySchool from "../../../hooks/useAcademicYearBySchool";
import axios from "../../../services/Api";
import {
  isDailyPlannerAcademicFaculty,
  isDailyPlannerCommonHod,
} from "./canAddDailyPlannerStudent";
import {
  extractDailyPlannerContent,
  extractStudentIds,
  extractStudents,
} from "./dailyPlannerUtils";

const userID = JSON.parse(sessionStorage.getItem("AcharyaErpUser"))?.userId;

function getSessionSchoolAndDept() {
  try {
    const userData = JSON.parse(sessionStorage.getItem("userData"));
    return {
      schoolId: userData?.school_id || null,
      deptId: userData?.dept_id || null,
    };
  } catch {
    return { schoolId: null, deptId: null };
  }
}

function extractSelectionIds(model) {
  if (!model) return [];
  if (Array.isArray(model)) {
    return model.map((id) => Number(id)).filter(Number.isFinite);
  }

  const ids = model.ids;
  if (ids instanceof Set) {
    return Array.from(ids).map((id) => Number(id)).filter(Number.isFinite);
  }
  if (Array.isArray(ids)) {
    return ids.map((id) => Number(id)).filter(Number.isFinite);
  }
  return [];
}

const AddStudentOnDailyPlanner = () => {
  const { plannerId } = useParams();
  const navigate = useNavigate();
  const setCrumbs = useBreadcrumbs();
  const { setAlertMessage, setAlertOpen } = useAlert();
  const numericPlannerId = Number(plannerId);
  const isAcademicFaculty = isDailyPlannerAcademicFaculty();
  const lockInstitutionalSchool =
    isAcademicFaculty && !isDailyPlannerCommonHod();
  const sessionSchoolAndDept = getSessionSchoolAndDept();

  const [plannerItem, setPlannerItem] = useState(null);
  const [isPlannerLoading, setIsPlannerLoading] = useState(true);
  const [schoolId, setSchoolId] = useState(
    isAcademicFaculty ? sessionSchoolAndDept.schoolId : null
  );
  const [deptId, setDeptId] = useState(sessionSchoolAndDept.deptId);
  const [acYearId, setAcYearId] = useState(null);
  const [programSpecializationId, setProgramSpecializationId] = useState(null);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [students, setStudents] = useState([]);
  const [isStudentsLoading, setIsStudentsLoading] = useState(false);
  const [assignedStudentIds, setAssignedStudentIds] = useState([]);
  const [isAssignedLoading, setIsAssignedLoading] = useState(true);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState("all");
  const hasHydratedAssignedRef = useRef(false);
  const hasUserSelectedSchoolRef = useRef(false);
  const [latestYearOptions, setLatestYearOptions] = useState([]);
  const [latestAcYearId, setLatestAcYearId] = useState(null);

  const { academicYearOptions, currentAcYearId } = useAcademicYearBySchool({
    schoolId: sessionSchoolAndDept.schoolId,
    autoFetch: Boolean(sessionSchoolAndDept.schoolId),
  });

  const isEditMode = assignedStudentIds.length > 0;
  const canAssignStudents = plannerItem?.student_status === true;
  const isInstitutional =
    plannerItem?.contribution_type === "Institutional Level";
  const canLoadStudents = isInstitutional
    ? isAcademicFaculty
      ? Boolean(schoolId && acYearId)
      : Boolean(acYearId)
    : Boolean(schoolId && deptId && acYearId);
  const yearOptions = academicYearOptions.length
    ? academicYearOptions
    : latestYearOptions;
  const selectedIdSet = useMemo(
    () => new Set(selectedStudentIds),
    [selectedStudentIds]
  );

  const studentRows = useMemo(() => {
    const seenIds = new Set();

    return students.flatMap((student) => {
      const studentId = student.student_id;
      if (studentId == null || seenIds.has(studentId)) return [];
      seenIds.add(studentId);

      return [
        {
          id: studentId,
          student_id: studentId,
          student_name: student.student_name || "Unknown",
          auid: student.auid ?? "",
          usn: student.usn ?? "",
        },
      ];
    });
  }, [students]);

  const visibleRows = useMemo(() => {
    if (scope === "selected") {
      return studentRows.filter((row) => selectedIdSet.has(row.id));
    }
    if (scope === "unselected") {
      return studentRows.filter((row) => !selectedIdSet.has(row.id));
    }
    return studentRows;
  }, [scope, studentRows, selectedIdSet]);

  const selectedInYearCount = useMemo(
    () => studentRows.filter((row) => selectedIdSet.has(row.id)).length,
    [studentRows, selectedIdSet]
  );

  const hasChanges = useMemo(() => {
    if (selectedStudentIds.length !== assignedStudentIds.length) return true;
    return assignedStudentIds.some((id) => !selectedIdSet.has(id));
  }, [selectedStudentIds, assignedStudentIds, selectedIdSet]);

  const columns = useMemo(
    () => [
      {
        field: "student_name",
        headerName: "Student Name",
        flex: 1,
      },
      {
        field: "auid",
        headerName: "AUID",
        flex: 1,
      },
      {
        field: "usn",
        headerName: "USN",
        flex: 1,
        valueGetter: (value, row) => row.usn || "-",
      },
    ],
    []
  );

  useEffect(() => {
    if (!numericPlannerId) {
      navigate("/daily-planner", { replace: true });
    }
  }, [numericPlannerId, navigate]);

  useEffect(() => {
    setCrumbs([
      { name: "Daily Planner", link: "/daily-planner" },
      { name: isEditMode ? "Edit Students" : "Add Student" },
    ]);
  }, [isEditMode, setCrumbs]);

  useEffect(() => {
    const defaultYearId = currentAcYearId || latestAcYearId;
    if (defaultYearId) {
      setAcYearId(defaultYearId);
    }
  }, [currentAcYearId, latestAcYearId]);

  useEffect(() => {
    if (sessionSchoolAndDept.schoolId) return undefined;

    let cancelled = false;

    const loadLatestAcademicYears = async () => {
      try {
        const response = await axios.get("/api/academic/academic_year");
        if (cancelled) return;

        const data = response.data.data || [];
        setLatestYearOptions(
          data.map((obj) => ({
            value: obj.ac_year_id,
            label: obj.ac_year,
          }))
        );

        const currentYears = data
          .map((obj) => obj.current_year)
          .filter((year) => Number.isFinite(year));
        if (!currentYears.length) return;

        const latestYear = Math.max(...currentYears);
        const latest = data.find((obj) => obj.current_year === latestYear);
        setLatestAcYearId(latest?.ac_year_id || null);
      } catch (err) {
        console.error(err);
      }
    };

    loadLatestAcademicYears();
    return () => {
      cancelled = true;
    };
  }, [sessionSchoolAndDept.schoolId]);

  useEffect(() => {
    if (isPlannerLoading) return;

    if (isInstitutional && lockInstitutionalSchool) {
      setSchoolId(sessionSchoolAndDept.schoolId ?? null);
      return;
    }

    if (hasUserSelectedSchoolRef.current) return;

    if (isInstitutional && !isAcademicFaculty) {
      setSchoolId(null);
      return;
    }

    if (sessionSchoolAndDept.schoolId) {
      setSchoolId((prev) => prev ?? sessionSchoolAndDept.schoolId);
    }
  }, [
    isPlannerLoading,
    isInstitutional,
    isAcademicFaculty,
    lockInstitutionalSchool,
    sessionSchoolAndDept.schoolId,
  ]);

  useEffect(() => {
    if (!userID || isPlannerLoading) return;

    let cancelled = false;

    const fetchEmployeeDetails = async () => {
      try {
        const response = await axios.get(
          `/api/employee/getEmployeeDetailsBasedOnUserID/${userID}`
        );
        if (cancelled) return;

        const data = response.data.data;

        if (data?.school_id && isInstitutional && lockInstitutionalSchool) {
          setSchoolId(data.school_id);
        } else {
          const canAutoselectSchool = isAcademicFaculty || !isInstitutional;

          if (
            data?.school_id &&
            canAutoselectSchool &&
            !hasUserSelectedSchoolRef.current
          ) {
            setSchoolId((prev) => prev ?? data.school_id);
          }
        }
        if (data?.dept_id) {
          setDeptId((prev) => prev ?? data.dept_id);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchEmployeeDetails();
    return () => {
      cancelled = true;
    };
  }, [isPlannerLoading, isInstitutional, isAcademicFaculty, lockInstitutionalSchool]);

  useEffect(() => {
    const fetchPlanner = async () => {
      if (!userID || !numericPlannerId) return;

      setIsPlannerLoading(true);
      try {
        const response = await axios.get(
          `/api/fetchAllDailyPlannerBasedOnUser?page=0&page_size=1000&sort=created_date&userId=${userID}`
        );
        const content = extractDailyPlannerContent(response.data);
        const item = content.find((row) => Number(row.id) === numericPlannerId);

        if (item?.is_approved === true) {
          setAlertMessage({
            severity: "error",
            message: "Students cannot be added after the task is approved",
          });
          setAlertOpen(true);
          navigate("/daily-planner", { replace: true });
          return;
        }

        setPlannerItem(item || null);
      } catch (err) {
        console.error(err);
        setPlannerItem(null);
      } finally {
        setIsPlannerLoading(false);
      }
    };

    fetchPlanner();
  }, [numericPlannerId]);

  useEffect(() => {
    if (!isInstitutional) {
      setSchoolOptions([]);
      return;
    }

    const getSchoolDetails = async () => {
      try {
        const res = await axios.get("/api/institute/school");
        setSchoolOptions(
          (res.data.data || []).map((obj) => ({
            value: obj.school_id,
            label: obj.school_name_short,
          }))
        );
      } catch (err) {
        console.error(err);
        setSchoolOptions([]);
      }
    };

    getSchoolDetails();
  }, [isInstitutional]);

  useEffect(() => {
    setProgramSpecializationId(null);

    if (!isInstitutional || !schoolId) {
      setProgramOptions([]);
      return;
    }

    const getPrograms = async () => {
      try {
        const { data: response } = await axios.get(
          `/api/academic/fetchAllProgramsWithSpecialization/${schoolId}`
        );
        setProgramOptions(
          (response.data || []).map((obj) => ({
            value: obj.program_specialization_id,
            label: `${obj.program_short_name} - ${obj.program_specialization_name}`,
          }))
        );
      } catch (err) {
        console.error(err);
        setProgramOptions([]);
      }
    };

    getPrograms();
  }, [isInstitutional, schoolId]);

  useEffect(() => {
    const fetchAssignedStudents = async () => {
      if (!numericPlannerId) return;

      setIsAssignedLoading(true);
      hasHydratedAssignedRef.current = false;
      setSelectedStudentIds([]);

      try {
        const response = await axios.get(
          `/api/getActiveStudentsByDailyPlannerId/${numericPlannerId}`
        );
        const ids = extractStudentIds(response.data);
        setAssignedStudentIds(ids);
      } catch (err) {
        console.error(err);
        setAssignedStudentIds([]);
      } finally {
        setIsAssignedLoading(false);
      }
    };

    fetchAssignedStudents();
  }, [numericPlannerId]);

  useEffect(() => {
    if (isAssignedLoading || hasHydratedAssignedRef.current) return;
    setSelectedStudentIds(assignedStudentIds);
    hasHydratedAssignedRef.current = true;
  }, [assignedStudentIds, isAssignedLoading]);

  useEffect(() => {
    const fetchStudents = async () => {
      if (!canAssignStudents || !canLoadStudents) {
        setStudents([]);
        return;
      }

      setIsStudentsLoading(true);
      setScope("all");
      try {
        const response = isInstitutional
          ? await axios.get("/api/student/studentBasicDetailsByProgramSpec", {
              params: {
                page: 0,
                sort: "student_id",
                pageSize: 1000000,
                acYearId,
                ...(schoolId ? { schoolId } : {}),
                ...(programSpecializationId
                  ? { programSpecializationId }
                  : {}),
              },
            })
          : await axios.get("/api/student/studentBasicDetailsByDept", {
              params: {
                page: 0,
                sort: "student_id",
                school_id: schoolId,
                pageSize: 1000000,
                acYearId,
                dept_id: deptId,
              },
            });
        setStudents(extractStudents(response.data));
      } catch (err) {
        console.error(err);
        setStudents([]);
        setAlertMessage({
          severity: "error",
          message: "Failed to load students",
        });
        setAlertOpen(true);
      } finally {
        setIsStudentsLoading(false);
      }
    };

    fetchStudents();
  }, [
    canAssignStudents,
    canLoadStudents,
    isInstitutional,
    schoolId,
    deptId,
    acYearId,
    programSpecializationId,
    setAlertMessage,
    setAlertOpen,
  ]);

  const handleSelectionChange = (model) => {
    const visibleIds = new Set(visibleRows.map((row) => Number(row.id)));
    const nextVisibleSelected = extractSelectionIds(model);

    setSelectedStudentIds((current) => {
      const keptFromOtherYears = current.filter((id) => !visibleIds.has(id));
      return [...keptFromOtherYears, ...nextVisibleSelected];
    });
  };

  const handleFilterTypeChange = (_event, newValue) => {
    if (newValue === null || newValue === scope) return;
    setScope(newValue);
  };

  const handleSubmit = async () => {
    if (!isEditMode && !selectedStudentIds.length) return;

    if (plannerItem?.is_approved === true) {
      setAlertMessage({
        severity: "error",
        message: "Students cannot be added after the task is approved",
      });
      setAlertOpen(true);
      return;
    }

    setSaving(true);
    try {
      await axios.post("/api/saveDailyPlannerStudents", {
        daily_planner_id: numericPlannerId,
        student_ids: selectedStudentIds,
      });
      setAlertMessage({
        severity: "success",
        message: "Students saved successfully",
      });
      setAlertOpen(true);
      navigate("/daily-planner");
    } catch (err) {
      console.error(err);
      setAlertMessage({
        severity: "error",
        message:
          err.response?.data?.message || "Failed to save students",
      });
      setAlertOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const StudentFilterToolbar = () => (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={scope}
      onChange={handleFilterTypeChange}
      aria-label="Student selection filter"
    >
      <ToggleButton value="all" sx={{ textTransform: "none", px: 1.25 }}>
        All
      </ToggleButton>
      <ToggleButton
        value="selected"
        sx={{
          textTransform: "none",
          px: 1.25,
          gap: 0.75,
          "&.Mui-selected": { color: "#2e7d32" },
        }}
      >
        <Box
          sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#4caf50" }}
        />
        Selected
      </ToggleButton>
      <ToggleButton
        value="unselected"
        sx={{
          textTransform: "none",
          px: 1.25,
          gap: 0.75,
          "&.Mui-selected": { color: "#546e7a" },
        }}
      >
        <Box
          sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#90a4ae" }}
        />
        Unselected
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const StudentToolbarActions = () => (
    <Chip
      size="small"
      color="primary"
      variant="outlined"
      label={
        selectedStudentIds.length > selectedInYearCount
          ? `${selectedInYearCount} selected · ${selectedStudentIds.length} total`
          : `${selectedInYearCount} selected`
      }
    />
  );

  const blockedMessage = !canAssignStudents
    ? "Student participation is not enabled for this task."
    : isInstitutional && isAcademicFaculty && !schoolId
      ? lockInstitutionalSchool
        ? "School is not available for this user."
        : "Select a school to load students."
      : isInstitutional && !acYearId
        ? "Select an academic year to load students."
        : !schoolId || !deptId
          ? "School or department is not available for this user."
          : "Select an academic year to load students.";

  const submitDisabled =
    isStudentsLoading ||
    isAssignedLoading ||
    !hasChanges ||
    (!isEditMode && !selectedStudentIds.length) ||
    saving;

  if (!numericPlannerId) return null;

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-start",
          gap: 2,
          mb: "15px",
          mt: "-40px",
        }}
      >
        {!isInstitutional && (
          <Box sx={{ width: 203 }}>
            <CustomAutocomplete
              name="acYearId"
              label="Academic Year"
              options={yearOptions}
              value={acYearId}
              handleChangeAdvance={(_, value) => setAcYearId(value)}
              required
            />
          </Box>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canAssignStudents || !canLoadStudents || submitDisabled}
        >
          {saving ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            `${isEditMode ? "Update" : "Add"} (${selectedStudentIds.length})`
          )}
        </Button>
      </Box>

      {canAssignStudents && isInstitutional && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <CustomAutocomplete
              name="acYearId"
              label="Academic Year"
              options={yearOptions}
              value={acYearId}
              handleChangeAdvance={(_, value) => setAcYearId(value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomAutocomplete
              name="schoolId"
              label="School"
              options={schoolOptions}
              value={schoolId}
              handleChangeAdvance={(_, value) => {
                if (lockInstitutionalSchool) return;
                hasUserSelectedSchoolRef.current = true;
                setSchoolId(value);
              }}
              required={isAcademicFaculty}
              disabled={lockInstitutionalSchool}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomAutocomplete
              name="programSpecializationId"
              label="Program"
              options={programOptions}
              value={programSpecializationId}
              handleChangeAdvance={(_, value) =>
                setProgramSpecializationId(value)
              }
              disabled={!schoolId}
            />
          </Grid>
        </Grid>
      )}

      {isPlannerLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !canAssignStudents || !canLoadStudents ? (
        <Alert severity="info">{blockedMessage}</Alert>
      ) : (
        <GridIndexStudentDetails
          rows={visibleRows}
          columns={columns}
          loading={isStudentsLoading || isAssignedLoading}
          checkboxSelection
          disableRowSelectionOnClick={false}
          keepNonExistentRowsSelected
          rowSelectionModel={selectedStudentIds.filter((id) =>
            studentRows.some((row) => row.id === id)
          )}
          onRowSelectionModelChange={handleSelectionChange}
          CustomToolbarComponent={StudentFilterToolbar}
          CustomToolbarActions={StudentToolbarActions}
        />
      )}
    </Box>
  );
};

export default AddStudentOnDailyPlanner;
